import React from "react";

import { ResilienceReport } from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import { K, useDomainName, useHalfLabel } from "./parts";
import {
  band,
  Domain,
  DOMAIN_THEMES,
  DOMAINS,
  formatScore,
  previousHalf,
  trend,
} from "./resilience-view";
import styles from "./resilience.module.scss";

// The prototype names two of each.
const NAMED = 2;

type Ranked = { domain: Domain; value: number };

/**
 * A fixed-template summary: how the Index moved since the previous half, the
 * strongest and lowest-scoring domains with their ranges, and the lowest theme
 * when it needs attention. Built only from figures the page already shows.
 */
const ResilienceRead = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const halfLabel = useHalfLabel();
  const selected = report.scores.selected;
  const tiles = selected.all_staff.domains;
  const score = (value: number) => formatScore(value, i18n.language);
  const bandName = (value: number) => t(`${K}.read.bands.${band(value)}`);

  const ranked = (domains: string[]): Ranked[] =>
    domains.slice(0, NAMED).map((domain) => {
      const tile = tiles[domain];
      if (tile.suppressed) {
        throw new Error(`A hidden domain was ranked: ${domain}`);
      }
      return { domain: domain as Domain, value: tile.value };
    });

  const range = (picked: Ranked[]) => {
    const [first, second] = picked;
    if (second === undefined || band(first.value) === band(second.value)) {
      return t(`${K}.read.range`, {
        count: picked.length,
        band: bandName(first.value),
      });
    }
    return t(`${K}.read.rangeMixed`, {
      first: domainName(first.domain),
      firstBand: bandName(first.value),
      second: domainName(second.domain),
      secondBand: bandName(second.value),
    });
  };

  const sentence = (key: "strengths" | "focus", domains: string[]) => {
    const picked = ranked(domains);
    if (picked.length === 0) return null;
    const [first, second] = picked.map(({ domain, value }) =>
      t(`${K}.read.domainScore`, {
        domain: domainName(domain),
        score: score(value),
      })
    );
    const listed =
      second === undefined ? first : t(`${K}.read.pair`, { first, second });
    return `${t(`${K}.read.${key}`, {
      count: picked.length,
      domains: listed,
    })} ${range(picked)}`;
  };

  const indexChange = () => {
    const change = report.scores.change.index;
    const now = selected.all_staff.index;
    const before = report.scores.previous.all_staff.index;
    if (change.suppressed || now.suppressed || before.suppressed) return null;
    return t(`${K}.read.change.${trend(change.value)}`, {
      index: score(now.value),
      half: halfLabel(report.period_value),
      previous: score(before.value),
      previousHalf: halfLabel(previousHalf(report.period_value)),
    });
  };

  // A domain with a single theme scores the same as the theme, so naming it
  // would only repeat the domain; a hidden domain's themes aren't on the page.
  const lowestTheme = () => {
    const themes = selected.all_staff.themes;
    const shown = DOMAINS.filter(
      (domain) => DOMAIN_THEMES[domain].length > 1 && !tiles[domain].suppressed
    ).flatMap((domain) =>
      DOMAIN_THEMES[domain].flatMap((theme) => {
        const tile = themes[theme];
        return tile.suppressed ? [] : [{ domain, theme, value: tile.value }];
      })
    );
    if (shown.length === 0) return null;
    const lowest = shown.reduce((low, one) =>
      one.value < low.value ? one : low
    );
    if (band(lowest.value) === "steady") return null;
    return t(`${K}.read.theme`, {
      theme: t(`${K}.themes.${lowest.theme}`),
      score: score(lowest.value),
      domain: domainName(lowest.domain),
    });
  };

  const domainSentences = [
    sentence("strengths", selected.strengths),
    sentence("focus", selected.focus_areas),
  ].filter((one): one is string => one !== null);
  if (domainSentences.length === 0) return null;

  const sentences = [indexChange(), ...domainSentences, lowestTheme()].filter(
    (one): one is string => one !== null
  );

  return (
    <div className={styles["read"]} data-testid="resilience-read">
      <h4 className={styles["read-title"]}>{t(`${K}.read.title`)}</h4>
      <p className={styles["read-text"]}>{sentences.join(" ")}</p>
    </div>
  );
};

export default ResilienceRead;
